import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  Container,
  Card,
  Avatar,
  Stack,
  Paper,
  Chip,
  Grid,
} from '@mui/material';
import {
  School as SchoolIcon,
  TrendingUp as TrendingUpIcon,
  Apple as AppleIcon,
  Android as AndroidIcon,
  Timer as TimerIcon,
  Psychology as BrainIcon,
  CalendarToday as CalendarIcon,
} from '@mui/icons-material';

const LandingPage = () => {
  // Geri sayım için state'ler
  const [days, setDays] = useState(0);
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(0);
  const [seconds, setSeconds] = useState(0);

  // LGS 2026 tarihi: 14 Haziran 2026 09:30 (GMT+3)
  // useMemo ile optimize edildi (react-hooks/exhaustive-deps uyarısı için)
  const lgsDate = useMemo(() => new Date('2026-06-14T09:30:00+03:00'), []);

  // Geri sayım fonksiyonu
  useEffect(() => {
    const updateCountdown = () => {
      const now = new Date();
      const difference = lgsDate.getTime() - now.getTime();

      if (difference > 0) {
        const d = Math.floor(difference / (1000 * 60 * 60 * 24));
        const h = Math.floor((difference % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
        const m = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));
        const s = Math.floor((difference % (1000 * 60)) / 1000);

        setDays(d);
        setHours(h);
        setMinutes(m);
        setSeconds(s);
      }
    };

    // İlk çalıştırma
    updateCountdown();
    
    // Her saniye güncelle
    const interval = setInterval(updateCountdown, 1000);
    
    // Cleanup
    return () => clearInterval(interval);
  }, [lgsDate]);
  
  // Geri sayım için tekli sayaç bileşeni
  const CountdownItem = ({ value, label }: { value: number; label: string }) => (
    <Paper
      elevation={0}
      sx={{
        minWidth: { xs: 70, sm: 90 },
        padding: { xs: 1, sm: 2 },
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.15)',
        backdropFilter: 'blur(10px)',
        borderRadius: 3,
        border: '1px solid rgba(255, 255, 255, 0.2)',
      }}
    >
      <Typography 
        variant="h3" 
        sx={{
          fontWeight: 700,
          color: 'white',
          textShadow: '0 2px 10px rgba(0,0,0,0.3)',
          fontSize: { xs: '2rem', sm: '2.5rem' }
        }}
      >
        {value < 10 ? `0${value}` : value}
      </Typography>
      <Typography 
        variant="body2" 
        sx={{
          color: 'rgba(255, 255, 255, 0.85)',
          fontWeight: 500,
          textTransform: 'uppercase',
          letterSpacing: 1
        }}
      >
        {label}
      </Typography>
    </Paper>
  );

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#FFFFFF' }}>
      {/* Hero Section - Completely Redesigned */}
      <Box sx={{
        background: 'linear-gradient(135deg, #4338CA 0%, #6D28D9 50%, #8B5CF6 100%)',
        color: 'white',
        position: 'relative',
        overflow: 'hidden',
        pt: { xs: 10, md: 12 },
        pb: { xs: 12, md: 16 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(255,255,255,0.15) 0%, transparent 30%), radial-gradient(circle at 80% 80%, rgba(255,255,255,0.1) 0%, transparent 30%)',
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          bottom: 0,
          left: 0,
          right: 0,
          height: '30%',
          background: 'linear-gradient(to top, rgba(0,0,0,0.2), transparent)',
        }
      }}>
        {/* Decorative Elements */}
        <Box sx={{
          position: 'absolute',
          top: '10%',
          right: '15%',
          width: { xs: 80, md: 120 },
          height: { xs: 80, md: 120 },
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(139,92,246,0.6) 0%, rgba(124,58,237,0.1) 70%)',
          filter: 'blur(40px)',
          opacity: 0.6,
        }} />
        <Box sx={{
          position: 'absolute',
          top: '60%',
          left: '10%',
          width: { xs: 60, md: 100 },
          height: { xs: 60, md: 100 },
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(239,68,68,0.5) 0%, rgba(220,38,38,0.1) 70%)',
          filter: 'blur(30px)',
          opacity: 0.6,
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center', 
            gap: 8,
            minHeight: '70vh',
            justifyContent: 'space-between'
          }}>
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 50%' }, textAlign: { xs: 'center', md: 'left' } }}>
              {/* LGS Badge */}
              <Chip
                label="LGS 2026'ya Hazırlık"
                color="primary"
                size="medium"
                icon={<CalendarIcon />}
                sx={{ 
                  mb: 4,
                  fontSize: { xs: '0.85rem', sm: '1rem' },
                  fontWeight: 600,
                  py: 1.5,
                  px: 1,
                  backgroundColor: 'rgba(255, 255, 255, 0.15)',
                  backdropFilter: 'blur(10px)',
                  color: 'white',
                  '& .MuiChip-icon': { color: 'white' },
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                }}
              />

              {/* Main Title */}
              <Typography variant="h1" sx={{ 
                fontWeight: 800, 
                mb: 3,
                fontSize: { xs: '3rem', sm: '3.5rem', md: '4.5rem' },
                background: 'linear-gradient(45deg, #fff 30%, rgba(255,255,255,0.8) 90%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 4px 15px rgba(0,0,0,0.2)',
                lineHeight: 1.1
              }}>
                LGSCİYİZ
              </Typography>
              
              {/* Subtitle with Gradual Reveal Animation */}
              <Typography variant="h4" sx={{ 
                mb: 6, 
                fontWeight: 400,
                lineHeight: 1.5,
                opacity: 0.95,
                fontSize: { xs: '1.3rem', sm: '1.6rem', md: '1.8rem' },
                textShadow: '0 2px 10px rgba(0,0,0,0.15)',
              }}>
                LGS'ye hazırlanan öğrenciler için<br/>
                <Box component="span" sx={{
                  fontWeight: 700,
                  background: 'linear-gradient(90deg, #FCD34D, #F59E0B)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}>
                  akıllı çalışma asistanı.
                </Box>
              </Typography>

              {/* LGS Countdown Section */}
              <Box sx={{ 
                mb: 6, 
                p: { xs: 3, sm: 4 }, 
                borderRadius: 4,
                background: 'rgba(0, 0, 0, 0.2)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.3)',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                  <TimerIcon sx={{ fontSize: 28, color: '#F59E0B' }} />
                  <Typography variant="h5" sx={{ fontWeight: 600 }}>
                    LGS 2026 Sınavına Kalan Süre
                  </Typography>
                </Box>

                <Stack 
                  direction="row" 
                  spacing={{ xs: 1, sm: 2 }}
                  sx={{ mb: 2, justifyContent: { xs: 'center', md: 'flex-start' } }}
                >
                  <CountdownItem value={days} label="Gün" />
                  <CountdownItem value={hours} label="Saat" />
                  <CountdownItem value={minutes} label="Dakika" />
                  <CountdownItem value={seconds} label="Saniye" />
                </Stack>

                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1, 
                  justifyContent: { xs: 'center', md: 'flex-start' } 
                }}>
                  <CalendarIcon sx={{ fontSize: 18, color: 'rgba(255, 255, 255, 0.7)' }} />
                  <Typography variant="body2" sx={{ 
                    color: 'rgba(255, 255, 255, 0.8)',
                    fontWeight: 400,
                  }}>
                    14 Haziran 2026, Pazar • 09:30
                  </Typography>
                </Box>
              </Box>

              {/* CTA Button */}
              <Stack 
                direction={{ xs: 'column', sm: 'row' }} 
                spacing={3}
                sx={{ justifyContent: { xs: 'center', md: 'flex-start' } }}
              >
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AppleIcon sx={{ fontSize: 24 }} />}
                  sx={{
                    background: 'linear-gradient(45deg, #000000 30%, #333333 90%)',
                    color: 'white',
                    borderRadius: 3,
                    py: 2.5,
                    px: 6,
                    fontSize: '1rem',
                    fontWeight: 600,
                    boxShadow: '0 10px 20px rgba(0, 0, 0, 0.2)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #111111 30%, #444444 90%)',
                      boxShadow: '0 15px 25px rgba(0, 0, 0, 0.3)',
                      transform: 'translateY(-2px)'
                    },
                    textTransform: 'none',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.9 }}>
                      İndir
                    </Typography>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
                      App Store
                    </Typography>
                  </Box>
                </Button>
                <Button
                  variant="contained"
                  size="large"
                  startIcon={<AndroidIcon sx={{ fontSize: 24 }} />}
                  sx={{
                    background: 'linear-gradient(45deg, #4CAF50 30%, #8BC34A 90%)',
                    color: 'white',
                    borderRadius: 3,
                    py: 2.5,
                    px: 6,
                    fontSize: '1rem',
                    fontWeight: 600,
                    boxShadow: '0 10px 20px rgba(76, 175, 80, 0.3)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #43A047 30%, #7CB342 90%)',
                      boxShadow: '0 15px 25px rgba(76, 175, 80, 0.4)',
                      transform: 'translateY(-2px)'
                    },
                    textTransform: 'none',
                    transition: 'all 0.3s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1.2 }}>
                    <Typography variant="caption" sx={{ fontSize: '0.7rem', opacity: 0.9 }}>
                      İndir
                    </Typography>
                    <Typography sx={{ fontSize: '1.1rem', fontWeight: 700 }}>
                      Google Play
                    </Typography>
                  </Box>
                </Button>
              </Stack>
            </Box>

            {/* Right Side - Modern Phone Mockup */}
            <Box sx={{ flex: { xs: '1 1 100%', md: '1 1 40%' } }}>
              <Box sx={{ 
                textAlign: 'center',
                position: 'relative',
                display: { xs: 'none', sm: 'block' }
              }}>
                {/* Phone Frame */}
                <Box sx={{
                  position: 'relative',
                  width: { xs: 280, sm: 320, md: 360 },
                  height: { xs: 560, sm: 640, md: 720 },
                  mx: 'auto',
                  borderRadius: '40px',
                  background: 'linear-gradient(to bottom, #2d3748 0%, #1a202c 100%)',
                  boxShadow: '0 40px 80px -20px rgba(0, 0, 0, 0.6), 0 0 0 2px rgba(255, 255, 255, 0.1)',
                  padding: '12px',
                  overflow: 'hidden',
                }}>
                  {/* Phone Screen */}
                  <Box sx={{
                    width: '100%',
                    height: '100%',
                    borderRadius: '32px',
                    overflow: 'hidden',
                    position: 'relative',
                  }}>
                    {/* Screen Content */}
                    <Box sx={{
                      width: '100%',
                      height: '100%',
                      backgroundColor: '#f8fafc',
                      display: 'flex',
                      flexDirection: 'column',
                    }}>
                      {/* App Header */}
                      <Box sx={{
                        p: 2,
                        background: 'linear-gradient(135deg, #4338CA 0%, #6D28D9 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                        <Typography variant="h6" sx={{ color: 'white', fontWeight: 600 }}>LGSCİYİZ</Typography>
                        <Avatar sx={{ width: 32, height: 32, fontSize: '0.875rem' }}>A</Avatar>
                      </Box>
                      
                      {/* App Content */}
                      <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {/* Welcome Card */}
                        <Card sx={{ p: 2, borderRadius: 3 }}>
                          <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            LGS Hazırlık
                          </Typography>
                          <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                            Günlük sorularla sınava hazırlanın
                          </Typography>
                          
                          {/* Subject Cards */}
                          <Stack direction="row" spacing={1} sx={{ mb: 2, flexWrap: 'wrap', gap: 1 }}>
                            {[
                              { name: 'Matematik', icon: '🔢', color: '#EF4444' },
                              { name: 'Fen Bilimleri', icon: '🧪', color: '#06B6D4' },
                              { name: 'Türkçe', icon: '📚', color: '#3B82F6' },
                              { name: 'Sosyal Bilgiler', icon: '🌍', color: '#10B981' },
                              { name: 'İngilizce', icon: '🌐', color: '#F59E0B' },
                            ].map((subject, i) => (
                              <Box key={i} sx={{ 
                                width: 60, 
                                height: 60, 
                                borderRadius: 2,
                                backgroundColor: `${subject.color}15`,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexDirection: 'column',
                                border: `1px solid ${subject.color}30`,
                              }}>
                                <Typography variant="h6">{subject.icon}</Typography>
                                <Typography variant="caption" sx={{ fontSize: '0.6rem' }}>
                                  {subject.name.substring(0, 3)}
                                </Typography>
                              </Box>
                            ))}
                          </Stack>
                          
                          {/* Daily Question Button */}
                          <Button 
                            variant="contained" 
                            fullWidth
                            sx={{ 
                              backgroundColor: '#4338CA',
                              borderRadius: 2,
                              fontWeight: 600,
                              fontSize: '0.9rem',
                              textTransform: 'none',
                            }}
                          >
                            Günün Sorularını Çöz
                          </Button>
                        </Card>
                        
                        {/* Stats Card */}
                        <Card sx={{ p: 2, borderRadius: 3 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                            Performansım
                          </Typography>
                          
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                            <Paper sx={{ flex: 1, p: 1, textAlign: 'center', bgcolor: '#f0fdf4' }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: '#10b981' }}>85%</Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Doğruluk</Typography>
                            </Paper>
                            
                            <Paper sx={{ flex: 1, p: 1, textAlign: 'center', bgcolor: '#eff6ff' }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: '#3b82f6' }}>#28</Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Sıralama</Typography>
                            </Paper>
                            
                            <Paper sx={{ flex: 1, p: 1, textAlign: 'center', bgcolor: '#fef2f2' }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: '#ef4444' }}>186</Typography>
                              <Typography variant="caption" sx={{ color: 'text.secondary' }}>Puan</Typography>
                            </Paper>
                          </Box>
                        </Card>
                        
                        {/* Latest Question */}
                        <Card sx={{ p: 2, borderRadius: 3, bgcolor: '#fdf2f8', borderLeft: '4px solid #db2777' }}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#db2777' }}>
                              Yeni Soru!
                            </Typography>
                            <Chip 
                              label="Matematik" 
                              size="small" 
                              sx={{ bgcolor: '#fce7f3', color: '#be185d', height: 24, fontSize: '0.7rem' }}
                            />
                          </Box>
                          <Typography variant="body2" sx={{ my: 1, fontSize: '0.85rem' }}>
                            5x² - 3x + 7 = 0 denkleminin kökleri nelerdir?
                          </Typography>
                          <Button 
                            size="small" 
                            sx={{ 
                              color: '#be185d', 
                              fontSize: '0.8rem', 
                              textTransform: 'none',
                              p: 0,
                              fontWeight: 600,
                            }}
                          >
                            Hemen Çöz
                          </Button>
                        </Card>
                      </Box>

                      {/* Bottom Navigation */}
                      <Box sx={{ 
                        display: 'flex', 
                        justifyContent: 'space-around', 
                        p: 1.5,
                        borderTop: '1px solid #e2e8f0',
                        backgroundColor: 'white'
                      }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <BrainIcon sx={{ fontSize: 22, color: '#4338CA' }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#4338CA', fontWeight: 600 }}>Sorular</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <TrendingUpIcon sx={{ fontSize: 22, color: '#64748b' }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Sıralama</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                          <SchoolIcon sx={{ fontSize: 22, color: '#64748b' }} />
                          <Typography variant="caption" sx={{ fontSize: '0.7rem' }}>Profil</Typography>
                        </Box>
                      </Box>
                    </Box>

                    {/* Notch */}
                    <Box sx={{
                      position: 'absolute',
                      top: 0,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      width: '40%',
                      height: 30,
                      backgroundColor: '#1a202c',
                      borderBottomLeftRadius: 20,
                      borderBottomRightRadius: 20,
                      zIndex: 5,
                    }} />
                  </Box>
                </Box>

                {/* Decorative Glow */}
                <Box sx={{
                  position: 'absolute',
                  bottom: -80,
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: 300,
                  height: 100,
                  background: 'radial-gradient(ellipse at center, rgba(67, 56, 202, 0.6) 0%, rgba(67, 56, 202, 0) 70%)',
                  filter: 'blur(40px)',
                  opacity: 0.6,
                  zIndex: -1,
                }} />
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* Footer - Enhanced footer with navigation */}
      <Box sx={{ py: 6, backgroundColor: '#1e293b', color: 'white' }}>
        <Container maxWidth="lg">
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4 }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
                LGSCİYİZ
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 3 }}>
                LGS'ye hazırlanan öğrenciler için akıllı çalışma asistanı. 
                Günlük sorular ve gerçek zamanlı sıralama ile başarıya ulaşın.
              </Typography>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                Hızlı Bağlantılar
              </Typography>
              <Stack spacing={1}>
                <Button 
                  color="inherit" 
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => window.location.href = '/about'}
                >
                  Hakkımızda
                </Button>
                <Button 
                  color="inherit" 
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => window.location.href = '/support'}
                >
                  Destek
                </Button>
                <Button 
                  color="inherit" 
                  sx={{ justifyContent: 'flex-start', textTransform: 'none' }}
                  onClick={() => window.location.href = '/privacy-policy'}
                >
                  Gizlilik Politikası
                </Button>
              </Stack>
            </Box>
            
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                İletişim
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)', mb: 1 }}>
                E-posta: destek@lgsciyiz.com
              </Typography>
              <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
                Destek: Pazartesi - Cuma, 09:00 - 18:00
              </Typography>
            </Box>
          </Box>
          
          <Box sx={{ 
            borderTop: '1px solid rgba(255,255,255,0.1)', 
            mt: 4, 
            pt: 3, 
            textAlign: 'center' 
          }}>
            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.8)' }}>
              © 2024 LGSCİYİZ • Tüm hakları saklıdır
            </Typography>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default LandingPage;